import React, { useState } from 'react';
import { buildSpamScorePreview } from './SpamScorePreview';

function parseHeader(raw, name) {
  const match = (raw || '').match(new RegExp(`^${name}:\\s*(.+)$`, 'im'));
  return match ? match[1].trim() : '';
}

function relTime(value) {
  if (!value) return '—';
  const diff = (Date.now() - new Date(value)) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(1)} hr`;
}

function classifyCategory(email) {
  const subject = parseHeader(email.raw, 'Subject').toLowerCase();
  const raw = (email.raw || '').toLowerCase();
  if (/(password|reset|otp|verify|login)/.test(subject) || /(password|reset|otp|verify|login)/.test(raw)) return 'Password Resets';
  if (/(invoice|receipt|payment|billing|order)/.test(subject) || /(invoice|receipt|payment|billing|order)/.test(raw)) return 'Invoices';
  if (/(welcome|signup|registration|activate|onboard)/.test(subject) || /(welcome|signup|registration|activate|onboard)/.test(raw)) return 'Welcome';
  if (/(promo|discount|newsletter|campaign|offer|marketing)/.test(subject) || /(promo|discount|newsletter|campaign|offer|marketing)/.test(raw)) return 'Marketing';
  return 'System Alerts';
}

function buildThroughputBuckets(emails) {
  const buckets = new Map();
  emails.forEach((email) => {
    const date = new Date(email.received_at);
    if (Number.isNaN(date.getTime())) return;
    const key = `${date.getMonth() + 1}/${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:00`;
    buckets.set(key, (buckets.get(key) || 0) + 1);
  });
  return Array.from(buckets.entries())
    .map(([label, count]) => ({ label, count }))
    .slice(-8);
}

function buildCategoryDistribution(emails) {
  const counts = new Map();
  emails.forEach((email) => {
    const category = classifyCategory(email);
    counts.set(category, (counts.get(category) || 0) + 1);
  });
  const total = emails.length || 1;
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count, pct: Math.round((count / total) * 100) }))
    .sort((a, b) => b.count - a.count);
}

function buildLatencyMetrics(emails) {
  const samples = emails
    .map((email) => {
      const headerDate = parseHeader(email.raw, 'Date');
      const sentAt = headerDate ? new Date(headerDate) : null;
      const receivedAt = email.received_at ? new Date(email.received_at) : null;
      if (!sentAt || !receivedAt || Number.isNaN(sentAt.getTime()) || Number.isNaN(receivedAt.getTime())) return null;
      return {
        id: email.id,
        subject: parseHeader(email.raw, 'Subject') || '(no subject)',
        latencySeconds: Math.max(0, (receivedAt.getTime() - sentAt.getTime()) / 1000)
      };
    })
    .filter(Boolean)
    .slice(0, 6);

  const avg = samples.length ? samples.reduce((sum, sample) => sum + sample.latencySeconds, 0) / samples.length : 0;
  return { samples, avg };
}

function buildSpamMetrics(emails) {
  const previews = emails.map((email) => {
    const parsed = {
      headers: {
        subject: parseHeader(email.raw, 'Subject'),
        from: parseHeader(email.raw, 'From'),
        'authentication-results': parseHeader(email.raw, 'Authentication-Results'),
        'received-spf': parseHeader(email.raw, 'Received-SPF')
      }
    };

    return buildSpamScorePreview({
      parsed,
      body: email.raw || '',
      htmlPart: email.html || '',
      attachments: email.attachments || []
    });
  });

  const average = previews.length ? previews.reduce((sum, item) => sum + item.score, 0) / previews.length : 0;
  return {
    average,
    rating: (average / 10).toFixed(1),
    highest: previews.reduce((max, item) => Math.max(max, item.score), 0)
  };
}

function buildActivityMetrics(buckets) {
  const ordered = buckets.map((bucket, index) => ({ ...bucket, index }));
  const spike = ordered.reduce((max, item) => (item.count > max.count ? item : max), { label: '—', count: 0 });
  const silent = ordered.reduce((min, item) => (item.count < min.count ? item : min), ordered[0] || { label: '—', count: 0 });
  return { buckets: ordered, spike, silent };
}

function buildDeliverabilityMetrics(emails) {
  const previews = emails.map((email) => {
    const authResults = parseHeader(email.raw, 'Authentication-Results');
    const receivedSpf = parseHeader(email.raw, 'Received-SPF');
    const preview = buildSpamScorePreview({
      parsed: {
        headers: {
          subject: parseHeader(email.raw, 'Subject'),
          from: parseHeader(email.raw, 'From'),
          'authentication-results': authResults,
          'received-spf': receivedSpf
        }
      },
      body: email.raw || '',
      htmlPart: email.html || '',
      attachments: email.attachments || []
    });

    const authPass = /(spf=pass|dkim=pass|dmarc=pass)/i.test(authResults) || /pass/i.test(receivedSpf);
    const bucket = preview.score >= 80 ? 'risk' : preview.score >= 45 || !authPass ? 'caution' : 'safe';
    return { score: preview.score, authPass, bucket };
  });

  const totals = previews.reduce((acc, item) => {
    acc[item.bucket] += 1;
    if (item.authPass) acc.authPass += 1;
    return acc;
  }, { safe: 0, caution: 0, risk: 0, authPass: 0 });

  const total = previews.length || 1;
  const ratio = (totals.safe + totals.caution * 0.5) / total;
  const score = Math.round(ratio * 100);
  const status = score >= 75 ? 'green' : score >= 45 ? 'yellow' : 'red';

  return {
    score,
    status,
    safe: totals.safe,
    caution: totals.caution,
    risk: totals.risk,
    authCoverage: Math.round((totals.authPass / total) * 100)
  };
}

function buildFormatMetrics(emails) {
  const totals = emails.reduce((acc, email) => {
    const raw = email.raw || '';
    const hasHtml = /content-type:\s*text\/html\b/i.test(raw);
    const hasPlain = /content-type:\s*text\/plain\b/i.test(raw);

    if (hasHtml && hasPlain) acc.both += 1;
    else if (hasHtml) acc.htmlOnly += 1;
    else if (hasPlain) acc.plainOnly += 1;
    else acc.empty += 1;

    return acc;
  }, { both: 0, htmlOnly: 0, plainOnly: 0, empty: 0 });

  const total = emails.length || 1;
  return {
    total: emails.length,
    items: [
      { key: 'both', label: 'HTML + Plain', count: totals.both, color: '#14b8a6', pct: Math.round((totals.both / total) * 100) },
      { key: 'htmlOnly', label: 'HTML Only', count: totals.htmlOnly, color: '#f59e0b', pct: Math.round((totals.htmlOnly / total) * 100) },
      { key: 'plainOnly', label: 'Plain Only', count: totals.plainOnly, color: '#60a5fa', pct: Math.round((totals.plainOnly / total) * 100) },
      { key: 'empty', label: 'No Body', count: totals.empty, color: '#94a3b8', pct: Math.round((totals.empty / total) * 100) },
    ]
  };
}

function buildDomainMetrics(emails) {
  const counts = new Map();
  emails.forEach((email) => {
    const from = parseHeader(email.raw, 'From');
    const match = from.match(/@([a-z0-9.-]+(?:\.[a-z0-9-]+)*)/i);
    if (!match) return;
    const domain = match[1].toLowerCase();
    counts.set(domain, (counts.get(domain) || 0) + 1);
  });

  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);
}

function ThroughputPanel({ buckets, C }) {
  const max = Math.max(...buckets.map((item) => item.count), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>Throughput Graph</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>Volume over time helps verify whether background jobs and launch traffic are hitting the sandbox as expected.</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(56px, 1fr))', gap: 10, alignItems: 'end', minHeight: 180 }}>
        {buckets.length === 0 ? (
          <div style={{ fontSize: 12, color: C.muted }}>No email activity yet.</div>
        ) : buckets.map((item) => (
          <div key={item.label} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
            <div style={{ width: '100%', height: `${Math.max((item.count / max) * 120, 8)}px`, borderRadius: 10, background: 'linear-gradient(180deg,#60a5fa,#6366f1)', border: `1px solid ${C.border}` }} />
            <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{item.count}</div>
            <div style={{ fontSize: 10, color: C.muted, textAlign: 'center' }}>{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpamPanel({ metrics, C }) {
  const score = Math.min(metrics.average, 100);
  const tone = score >= 80 ? C.danger : score >= 40 ? '#f59e0b' : C.green;

  // SVG gauge: semicircle arc from left (9 o'clock) to right (3 o'clock) through the top.
  // angle θ = π*(1 - score/100): θ=π at 0 (left), θ=0 at 100 (right).
  const R = 88, cx = 110, cy = 118;
  const theta = Math.PI * (1 - score / 100);
  const ex = cx + R * Math.cos(theta);
  const ey = cy - R * Math.sin(theta);
  const nx = cx + (R - 14) * Math.cos(theta);
  const ny = cy - (R - 14) * Math.sin(theta);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 20, alignItems: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <svg width="220" height="140" viewBox="0 0 220 140" overflow="visible">
          {/* Track (full arc, left → top → right, clockwise, sweep=1) */}
          <path d={`M ${cx - R} ${cy} A ${R} ${R} 0 0 1 ${cx + R} ${cy}`}
            fill="none" stroke={C.border} strokeWidth="14" strokeLinecap="round" />
          {/* Filled arc */}
          {score > 0 && (
            <path d={`M ${cx - R} ${cy} A ${R} ${R} 0 0 1 ${ex.toFixed(2)} ${ey.toFixed(2)}`}
              fill="none" stroke={tone} strokeWidth="14" strokeLinecap="round" />
          )}
          {/* Needle */}
          <line x1={cx} y1={cy} x2={nx.toFixed(2)} y2={ny.toFixed(2)}
            stroke={tone} strokeWidth="2.5" strokeLinecap="round" />
          <circle cx={cx} cy={cy} r="5" fill={tone} />
          {/* End labels */}
          <text x={cx - R - 6} y={cy + 16} textAnchor="middle" fontSize="10" fill={C.muted}>0</text>
          <text x={cx + R + 6} y={cy + 16} textAnchor="middle" fontSize="10" fill={C.muted}>10</text>
          {/* Center text */}
          <text x={cx} y={cy - 46} textAnchor="middle" fontSize="9" fill={C.muted} letterSpacing="1.5">SPAM SCORE</text>
          <text x={cx} y={cy - 16} textAnchor="middle" fontSize="38" fontWeight="800" fill={tone}>{metrics.rating}</text>
          <text x={cx} y={cy - 2} textAnchor="middle" fontSize="10" fill={C.muted}>out of 10</text>
        </svg>
      </div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 8 }}>Spam & Quality Score</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 14 }}>This preview estimates inbox health before messages go to real customers. It highlights risky subjects, content, links, and attachments.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Average Score</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: tone }}>{metrics.average.toFixed(0)}</div>
          </div>
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Highest Risk</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: C.text }}>{metrics.highest}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryPanel({ distribution, C }) {
  const palette = ['#6366f1', '#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 20, alignItems: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 220, height: 220, borderRadius: '50%', background: `conic-gradient(${distribution.map((item, index) => `${palette[index % palette.length]} 0 ${item.pct}%`).join(', ')})`, position: 'relative' }}>
          <div style={{ position: 'absolute', inset: 42, borderRadius: '50%', background: C.panel, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <div style={{ fontSize: 12, color: C.muted }}>Categories</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: C.text }}>{distribution.length}</div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Email Category Distribution</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 4 }}>See which parts of the product are generating the most email traffic so you can spot noisy services or missing categories.</div>
        {distribution.length === 0 ? (
          <div style={{ fontSize: 12, color: C.muted }}>No categories available yet.</div>
        ) : distribution.map((item, index) => (
          <div key={item.label} style={{ display: 'grid', gridTemplateColumns: '18px 1fr auto auto', gap: 10, alignItems: 'center', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: palette[index % palette.length] }} />
            <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{item.label}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{item.count} emails</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{item.pct}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LatencyPanel({ metrics, C }) {
  const max = Math.max(...metrics.samples.map((sample) => sample.latencySeconds), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>Technical Latency</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>Estimated time between the email `Date` header and when the sandbox stored the message. Use it to spot SMTP or network bottlenecks.</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Average Latency</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.text }}>{fmtDuration(metrics.avg)}</div>
        </div>
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Measured Samples</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.text }}>{metrics.samples.length}</div>
        </div>
      </div>
      {metrics.samples.length === 0 ? (
        <div style={{ fontSize: 12, color: C.muted }}>No usable `Date` headers yet, so latency cannot be estimated.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {metrics.samples.map((sample) => (
            <div key={sample.id} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{sample.subject}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{fmtDuration(sample.latencySeconds)}</div>
              </div>
              <div style={{ height: 10, background: C.border, borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${Math.max((sample.latencySeconds / max) * 100, 6)}%`, height: '100%', background: 'linear-gradient(90deg,#14b8a6,#60a5fa)', borderRadius: 999 }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityFeedPanel({ metrics, C }) {
  const width = 520;
  const height = 180;
  const max = Math.max(...metrics.buckets.map((item) => item.count), 1);
  const points = metrics.buckets.map((item, index) => {
    const x = metrics.buckets.length === 1 ? width / 2 : (index / (metrics.buckets.length - 1)) * (width - 24) + 12;
    const y = height - ((item.count / max) * (height - 28) + 14);
    return `${x},${y}`;
  }).join(' ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>Activity Feed</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>Track hourly movement to spot spikes in automated sends and quiet periods where expected mail is missing.</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Spike Window</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>{metrics.spike.label}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{metrics.spike.count} emails captured</div>
        </div>
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Quiet Window</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>{metrics.silent.label}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{metrics.silent.count} emails captured</div>
        </div>
      </div>
      {metrics.buckets.length === 0 ? (
        <div style={{ fontSize: 12, color: C.muted }}>No email activity yet.</div>
      ) : (
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18 }}>
          <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 220 }}>
            {[0.25, 0.5, 0.75, 1].map((step) => {
              const y = height - step * (height - 28) - 14;
              return <line key={step} x1="12" x2={String(width - 12)} y1={String(y)} y2={String(y)} stroke={C.border} strokeWidth="1" />;
            })}
            <polyline fill="none" stroke="#60a5fa" strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" points={points} />
            {metrics.buckets.map((item, index) => {
              const x = metrics.buckets.length === 1 ? width / 2 : (index / (metrics.buckets.length - 1)) * (width - 24) + 12;
              const y = height - ((item.count / max) * (height - 28) + 14);
              return <circle key={item.label} cx={String(x)} cy={String(y)} r="5" fill="#6366f1" stroke={C.panel} strokeWidth="2" />;
            })}
          </svg>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(72px, 1fr))', gap: 8, marginTop: 10 }}>
            {metrics.buckets.map((item) => (
              <div key={item.label} style={{ fontSize: 10, color: C.muted, textAlign: 'center' }}>{item.label}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DeliverabilityPanel({ metrics, C }) {
  const tone = metrics.status === 'green' ? C.green : metrics.status === 'yellow' ? '#f59e0b' : C.danger;
  const label = metrics.status === 'green' ? 'Safe' : metrics.status === 'yellow' ? 'Review' : 'Risk';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 20, alignItems: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 220, height: 220, borderRadius: '50%', background: `conic-gradient(${tone} 0deg ${metrics.score * 1.8}deg, ${C.border} ${metrics.score * 1.8}deg 180deg, transparent 180deg 360deg)`, position: 'relative' }}>
          <div style={{ position: 'absolute', inset: 24, borderRadius: '50%', background: C.panel, border: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Deliverability</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: tone }}>{label}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{metrics.score}/100 readiness</div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>Deliverability Check</div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>Use this gauge as a quick answer to whether the current inbox sample looks safe enough to mirror into production mail flows.</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Safe</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>{metrics.safe}</div>
          </div>
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Needs Review</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>{metrics.caution}</div>
          </div>
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>High Risk</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>{metrics.risk}</div>
          </div>
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Auth Coverage</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>{metrics.authCoverage}%</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormatPanel({ metrics, C }) {
  const nonZero = metrics.items.filter((item) => item.count > 0);
  const segments = (nonZero.length ? nonZero : metrics.items.slice(0, 1)).reduce((acc, item, index, array) => {
    const start = acc.offset;
    const span = array.length === 1 ? 100 : item.pct;
    acc.parts.push(`${item.color} ${start}% ${Math.min(start + span, 100)}%`);
    acc.offset = Math.min(start + span, 100);
    return acc;
  }, { offset: 0, parts: [] });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 20, alignItems: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 220, height: 220, borderRadius: '50%', background: `conic-gradient(${segments.parts.join(', ')}, ${C.border} 100% 100%)`, position: 'relative' }}>
          <div style={{ position: 'absolute', inset: 40, borderRadius: '50%', background: C.panel, border: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 12, color: C.muted }}>Messages</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: C.text }}>{metrics.total}</div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>HTML vs Plain Text</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 4 }}>Check whether your test mail includes both formats so client previews, accessibility, and fallback rendering stay aligned.</div>
        {metrics.items.map((item) => (
          <div key={item.key} style={{ display: 'grid', gridTemplateColumns: '18px 1fr auto auto', gap: 10, alignItems: 'center', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: item.color }} />
            <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{item.label}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{item.count} emails</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{item.pct}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DomainPanel({ domains, C }) {
  const max = Math.max(...domains.map((item) => item.count), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>Common Domains</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>See which sender domains are most active in the sandbox so you can spot dominant test tenants or missing expected traffic.</div>
      </div>
      {domains.length === 0 ? (
        <div style={{ fontSize: 12, color: C.muted }}>No sender domains found in the captured email headers yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {domains.map((item) => (
            <div key={item.label} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{item.label}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{item.count}</div>
              </div>
              <div style={{ height: 12, background: C.border, borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${Math.max((item.count / max) * 100, 8)}%`, height: '100%', background: 'linear-gradient(90deg,#f59e0b,#ef4444)', borderRadius: 999 }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SmtpLogsPanel({ logs, loading, err }) {
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(new Set());

  const LEVEL_COLOR = { INFO: '#3b82f6', SUCCESS: '#22c55e', WARN: '#f59e0b', WARNING: '#f59e0b', ERROR: '#ef4444' };
  const LEVEL_ICON  = { INFO: 'ⓘ', SUCCESS: '✓', WARN: '⚠', WARNING: '⚠', ERROR: '⊘' };

  const normalizeLevel = (l) => (l === 'WARN' ? 'WARNING' : l);

  const filteredLogs = logs.filter(log => {
    const level = normalizeLevel(log.level);
    if (filter !== 'ALL' && level !== filter) return false;
    if (search && !log.message.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const latestSession = logs.length > 0 ? logs[0].connection_id : null;

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleExport = () => {
    const content = filteredLogs.map((log, i) =>
      `${i + 1}\t${log.level}\t${log.message}\t${new Date(log.created_at).toLocaleString()}`
    ).join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'smtp-logs.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  const TERM = {
    bg:     '#0d1117',
    panel:  '#161b22',
    border: '#30363d',
    border2:'#21262d',
    text:   '#e6edf3',
    muted:  '#7d8590',
    font:   "'Consolas', 'Courier New', monospace",
  };

  return (
    <div style={{ fontFamily: TERM.font, background: TERM.bg, borderRadius: 14, overflow: 'hidden', border: `1px solid ${TERM.border}` }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 18px', background: TERM.panel, borderBottom: `1px solid ${TERM.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, background: TERM.bg, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${TERM.border}`, fontSize: 16, color: '#3b82f6' }}>
            &gt;_
          </div>
          <div>
            <div style={{ fontSize: 11, color: TERM.muted, display: 'flex', alignItems: 'center', gap: 8 }}>
                SMTP Server Console
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#0f2a1a', border: '1px solid #22c55e44', borderRadius: 20, padding: '5px 12px' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e99' }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#22c55e' }}>LIVE</span>
          </div>
        </div>
      </div>

      {/* ── Filter toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: TERM.bg, borderBottom: `1px solid ${TERM.border2}`, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['ALL', 'INFO', 'WARNING', 'ERROR', 'SUCCESS'].map(level => {
            const isActive = filter === level;
            const c = level === 'ALL' ? TERM.text : (LEVEL_COLOR[level] || TERM.text);
            return (
              <button key={level} onClick={() => setFilter(level)} style={{
                background: isActive ? (level === 'ALL' ? TERM.text : `${c}22`) : 'transparent',
                color: isActive && level === 'ALL' ? TERM.bg : c,
                border: `1px solid ${isActive ? (level === 'ALL' ? TERM.text : c) : TERM.border}`,
                borderRadius: 6, padding: '5px 11px', fontSize: 11, fontWeight: 600,
                cursor: 'pointer', fontFamily: TERM.font, letterSpacing: '0.3px',
              }}>
                {level}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1, minWidth: 160, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: TERM.muted, fontSize: 12, pointerEvents: 'none' }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filter logs by keyword or regex..."
            style={{ width: '100%', boxSizing: 'border-box', background: TERM.panel, border: `1px solid ${TERM.border}`, borderRadius: 7, padding: '6px 10px 6px 28px', fontSize: 12, color: TERM.text, outline: 'none', fontFamily: TERM.font }}
          />
        </div>
        <button onClick={() => { setSearch(''); setFilter('ALL'); }} style={{ background: 'transparent', border: `1px solid ${TERM.border}`, color: TERM.muted, borderRadius: 6, padding: '5px 11px', fontSize: 11, cursor: 'pointer', fontFamily: TERM.font, display: 'flex', alignItems: 'center', gap: 5 }}>
          🗑 Clear
        </button>
        <button onClick={handleExport} style={{ background: 'transparent', border: `1px solid ${TERM.border}`, color: TERM.muted, borderRadius: 6, padding: '5px 11px', fontSize: 11, cursor: 'pointer', fontFamily: TERM.font, display: 'flex', alignItems: 'center', gap: 5 }}>
          ↓ Export
        </button>
      </div>

      {/* ── Log rows grouped by connection_id ── */}
      <div style={{ minHeight: 160 }}>
        {loading ? (
          <div style={{ padding: '22px 18px', fontSize: 12, color: TERM.muted }}>Loading SMTP logs…</div>
        ) : err ? (
          <div style={{ padding: '22px 18px', fontSize: 12, color: '#ef4444' }}>{err}</div>
        ) : filteredLogs.length === 0 ? (
          <div style={{ padding: '22px 18px', fontSize: 12, color: TERM.muted }}>No SMTP logs match this filter.</div>
        ) : (() => {
          // Group filtered logs by connection_id preserving insertion order
          const groups = filteredLogs.reduce((acc, log) => {
            const key = log.connection_id || 'unknown';
            if (!acc.has(key)) acc.set(key, []);
            acc.get(key).push(log);
            return acc;
          }, new Map());

          return Array.from(groups.entries()).map(([connId, entries], groupIdx) => (
            <div key={connId} style={{ borderBottom: groupIdx < groups.size - 1 ? `2px solid ${TERM.border}` : 'none' }}>
              {/* Session separator header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', background: TERM.panel, borderBottom: `1px solid ${TERM.border2}` }}>
                <span style={{ fontSize: 10, color: TERM.muted, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Session</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', fontFamily: TERM.font }}>{connId}</span>
                <span style={{ fontSize: 10, color: TERM.muted, background: TERM.bg, border: `1px solid ${TERM.border}`, borderRadius: 10, padding: '1px 7px' }}>{entries.length} events</span>
              </div>
              {/* Rows within this session */}
              {entries.map((log, i) => {
                const level = normalizeLevel(log.level);
                const color = LEVEL_COLOR[level] || TERM.muted;
                const icon  = LEVEL_ICON[level]  || 'ⓘ';
                const isExp = expanded.has(log.id);
                return (
                  <div
                    key={log.id}
                    onClick={() => toggleExpand(log.id)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px 110px 1fr auto',
                      alignItems: 'center',
                      padding: '9px 14px',
                      borderBottom: `1px solid ${TERM.border2}`,
                      cursor: 'pointer',
                      background: isExp ? TERM.panel : 'transparent',
                    }}
                  >
                    <span style={{ fontSize: 11, color: TERM.muted, fontWeight: 600, textAlign: 'right', paddingRight: 12 }}>{i + 1}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color, background: `${color}18`,
                      border: `1px solid ${color}44`, borderRadius: 5,
                      padding: '3px 8px', whiteSpace: 'nowrap', letterSpacing: '0.3px',
                      textAlign: 'center', display: 'inline-block',
                    }}>
                      {icon} {level}
                    </span>
                    <span style={{ fontSize: 12, color: TERM.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: 10 }}>
                      {log.message}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 14, whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 11, color: TERM.muted }}>🕐 {new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ));
        })()}
      </div>

      {/* ── Footer ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 18px', background: TERM.panel, borderTop: `1px solid ${TERM.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
          <span style={{ fontSize: 11, color: TERM.muted }}>System Healthy</span>
        </div>
        <span style={{ fontSize: 11, color: TERM.muted }}>{filteredLogs.length} entries shown</span>
        <span style={{ fontSize: 11, color: TERM.muted }}>v1.0.0 | worker-01</span>
      </div>
    </div>
  );
}

export default function DashboardPage({ emails, planData, smtpLogs, smtpLogsLoading, smtpLogsErr, C, STYLES }) {
  const [activePanel, setActivePanel] = useState('throughput');
  const throughputBuckets = buildThroughputBuckets(emails);
  const categoryDistribution = buildCategoryDistribution(emails);
  const latencyMetrics = buildLatencyMetrics(emails);
  const spamMetrics = buildSpamMetrics(emails);
  const activityMetrics = buildActivityMetrics(throughputBuckets);
  const deliverabilityMetrics = buildDeliverabilityMetrics(emails);
  const formatMetrics = buildFormatMetrics(emails);
  const domainMetrics = buildDomainMetrics(emails);

  const submenus = [
    { key: 'throughput', label: '📈 Throughput Graph' },
    { key: 'spam', label: '🛡 Spam & Quality Score' },
    { key: 'category', label: '🍩 Category Distribution' },
    { key: 'latency', label: '⚡ Technical Latency' },
    { key: 'activity', label: '📉 Activity Feed' },
    { key: 'deliverability', label: '🚦 Deliverability Check' },
    { key: 'format', label: '🧩 HTML vs Plain Text' },
    { key: 'domains', label: '🌐 Common Domains' },
    { key: 'smtp-logs', label: '🧾 SMTP Logs' },
  ];

  return (
    <div style={STYLES.accountWrap}>
      <div style={STYLES.accountTitle}>📊 Dashboard</div>
      <div style={STYLES.accountSub}>Quick view of inbox activity, plan usage, and account status.</div>

      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)' }}>
          <div style={{ padding: 18, borderRight: `1px solid ${C.border}`, background: C.bg, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {submenus.map((submenu) => (
              <button
                key={submenu.key}
                type="button"
                onClick={() => setActivePanel(submenu.key)}
                style={{
                  border: `1px solid ${activePanel === submenu.key ? C.accent : C.border}`,
                  background: activePanel === submenu.key ? `${C.accent}22` : C.panel,
                  color: activePanel === submenu.key ? C.text : C.muted,
                  borderRadius: 12,
                  padding: '12px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                {submenu.label}
              </button>
            ))}
          </div>

          <div style={{ padding: 18 }}>
            {activePanel === 'throughput' && <ThroughputPanel buckets={throughputBuckets} C={C} />}
            {activePanel === 'spam' && <SpamPanel metrics={spamMetrics} C={C} />}
            {activePanel === 'category' && <CategoryPanel distribution={categoryDistribution} C={C} />}
            {activePanel === 'latency' && <LatencyPanel metrics={latencyMetrics} C={C} />}
            {activePanel === 'activity' && <ActivityFeedPanel metrics={activityMetrics} C={C} />}
            {activePanel === 'deliverability' && <DeliverabilityPanel metrics={deliverabilityMetrics} C={C} />}
            {activePanel === 'format' && <FormatPanel metrics={formatMetrics} C={C} />}
            {activePanel === 'domains' && <DomainPanel domains={domainMetrics} C={C} />}
            {activePanel === 'smtp-logs' && <SmtpLogsPanel logs={smtpLogs} loading={smtpLogsLoading} err={smtpLogsErr} C={C} />}
          </div>
        </div>
      </div>
    </div>
  );
}
